import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createColumnHelper } from "@tanstack/react-table";
import { Pencil, Trash2 } from "lucide-react";
import Button from "../../../../../../components/shared/Button";
import { useIsMasterCompany } from "../../../../../../utils/hooks/user";
import { useCompanyCurrency } from "../../../../../../utils/hooks/company";
import { renderSettingsResourceLabel } from "../../../../../../utils/helpers/settings";
import { currencyFormatter } from "../../../../../../utils/helpers/currencyFormatter";

const columnHelper = createColumnHelper();

export const useColumns = ({ edit, remove, roles }: any) => {
	const { t } = useTranslation();
	const isMasterCompany = useIsMasterCompany();
	const currency = useCompanyCurrency();

	return useMemo(() => {
		const columns: any = [
			columnHelper.accessor("role", {
				header: t("role"),
				enableColumnFilter: true,
				cell: (info) => {
					const value = info.getValue();
					const role = roles?.find(({ id }: any) => id === value);
					return (value && role) && renderSettingsResourceLabel(role);
				},
			}),
			columnHelper.accessor("maxRate", {
				header: t("max-rate"),
				enableColumnFilter: true,
				cell: (info) => {
					const value = info.getValue() || 0;
					return currencyFormatter(value, currency);
				},
			}),
			columnHelper.accessor("maxAmount", {
				header: t("max-amount"),
				enableColumnFilter: true,
				cell: (info) => {
					const value = info.getValue() || 0;
					return currencyFormatter(value, currency);
				},
			}),
		];

		if (isMasterCompany && (edit || remove)) {
			columns.push(columnHelper.display({
				id: "actions",
				header: () => <div className="tw-text-end">{t("actions")}</div>,
				cell: (info: any) => {
					const data = info.row.original;
					return (
						<div className="tw-flex tw-justify-end tw-gap-1">
							{edit && (
								<Button
									type="button"
									variant="ghost"
									onClick={() => edit(data)}
									tooltip={t("edit")}
								>
									<Pencil className="tw-h-4 tw-w-4" />
								</Button>
							)}
							{remove && (
								<Button
									type="button"
									variant="ghost"
									onClick={() => remove(data)}
									tooltip={t("delete")}
								>
									<Trash2 className="tw-h-4 tw-w-4 tw-text-destructive" />
								</Button>
							)}
						</div>
					);
				},
			}));
		}

		return columns;
	}, [isMasterCompany, currency, edit, remove, roles, t]);
};
